/*
 * Lumera ürün sayfası (product.lumera) etkileşimleri. Bağımlılıksız web component'ler.
 *   <lum-price>        varyant değişince fiyat / indirim rozetini günceller
 *   <lum-countdown>    ziyaretçi başına teklif sayacı; sıfırda yenileme mesajı + yeniden başlar
 *   <lum-review-rail>  kayan yorum rayı: duraklat düğmesi, ekran dışında durur
 *   <lum-video-rail>   dokun-oynat videolar (aynı anda tek), ok düğmeleriyle kaydırma
 *   <lum-review-grid>  dengeli sütunlar + "Daha fazla yorum göster"
 *   [data-lum-stats]   puan özeti; Storefront API ile yorum listesinin tamamından yeniden hesaplanır
 *   <lum-cta-atc>      sayfa içi "Sepete ekle" düğmeleri ana ürün formunu kullanır
 *   [data-lum-lightbox] yorum görselini <dialog> içinde büyütür
 */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const define = (name, cls) => {
    if (!customElements.get(name)) customElements.define(name, cls);
  };

  /** Ana ürün formundaki seçili varyant kimliği */
  const mainVariantInput = () =>
    document.querySelector('.product-information product-form-component form input[name="id"]') ||
    document.querySelector('product-form-component form input[name="id"]');

  /* ---------------- Fiyat ---------------- */
  class LumPrice extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-lum-price-json]');
      if (!json) return;
      try {
        this.variants = JSON.parse(json.textContent);
      } catch {
        return;
      }
      this.onChange = () => window.setTimeout(() => this.#sync(), 60);
      document.addEventListener('change', this.onChange);
    }

    disconnectedCallback() {
      if (this.onChange) document.removeEventListener('change', this.onChange);
    }

    #sync() {
      const input = mainVariantInput();
      const data = input && this.variants[input.value];
      if (!data) return;
      const was = this.querySelector('[data-lum-price-was]');
      const deal = this.querySelector('[data-lum-price-deal]');
      this.querySelector('[data-lum-price-now]').textContent = data.now;
      const onSale = Boolean(data.was);
      was.hidden = !onSale;
      deal.hidden = !onSale;
      if (onSale) {
        was.textContent = data.was;
        this.querySelector('[data-lum-price-pct]').textContent = `%${data.pct} İNDİRİM`;
        this.querySelector('[data-lum-price-save]').textContent = `${data.save} kazanç`;
      }
      this.querySelector('[data-lum-price-label]').textContent = onSale
        ? `İndirimli fiyat ${data.now}, önceki fiyat ${data.was}`
        : `Fiyat ${data.now}`;
    }
  }

  /* ---------------- Teklif sayacı ---------------- */
  const CD_KEY = 'lum_offer_end';
  const store = {
    get() {
      try {
        return Number(localStorage.getItem(CD_KEY)) || 0;
      } catch {
        return 0;
      }
    },
    set(v) {
      try {
        localStorage.setItem(CD_KEY, String(v));
      } catch {
        /* gizli pencere: sayaç her yüklemede baştan başlar */
      }
    },
  };

  class LumCountdown extends HTMLElement {
    connectedCallback() {
      this.span = Math.max(1, Number(this.dataset.minutes) || 30) * 60000;
      this.h = this.querySelector('[data-h]');
      this.m = this.querySelector('[data-m]');
      this.s = this.querySelector('[data-s]');
      this.renew = this.querySelector('[data-renew]');
      this.renewText = this.querySelector('[data-renew-text]')?.innerHTML.trim() || '';
      const now = Date.now();
      const saved = store.get();
      this.end = saved > now && saved - now <= this.span ? saved : now + this.span;
      store.set(this.end);
      this.tick = this.tick.bind(this);
      this.tick();
      this.timer = window.setInterval(this.tick, 1000);
      this.onVisible = () => document.visibilityState === 'visible' && this.tick();
      document.addEventListener('visibilitychange', this.onVisible);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
      window.clearTimeout(this.hideTimer);
      document.removeEventListener('visibilitychange', this.onVisible);
    }

    tick() {
      let left = this.end - Date.now();
      if (left <= 0) {
        this.end = Date.now() + this.span;
        store.set(this.end);
        left = this.span;
        this.#announceRenewal();
      }
      const total = Math.floor(left / 1000);
      const pad = (n) => String(n).padStart(2, '0');
      this.h.textContent = pad(Math.floor(total / 3600));
      this.m.textContent = pad(Math.floor((total % 3600) / 60));
      this.s.textContent = pad(total % 60);
    }

    #announceRenewal() {
      if (!this.renew || !this.renewText) return;
      this.renew.textContent = this.renewText;
      this.classList.add('is-renewed');
      window.clearTimeout(this.hideTimer);
      this.hideTimer = window.setTimeout(() => this.classList.remove('is-renewed'), 4500);
    }
  }

  /* ---------------- Yorum rayı ---------------- */
  class LumReviewRail extends HTMLElement {
    connectedCallback() {
      const btn = this.querySelector('[data-lum-rail-toggle]');
      if (btn) {
        btn.addEventListener('click', () => {
          const paused = this.classList.toggle('is-paused');
          btn.setAttribute('aria-pressed', String(paused));
          btn.querySelector('[data-label-pause]').hidden = paused;
          btn.querySelector('[data-label-play]').hidden = !paused;
        });
      }
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(([entry]) => {
          this.classList.toggle('is-offscreen', !entry.isIntersecting);
        });
        this.io.observe(this);
      }
    }

    disconnectedCallback() {
      this.io?.disconnect();
    }
  }

  /* ---------------- Video / görsel rayı ---------------- */
  const pauseAllExcept = (keep) => {
    for (const v of document.querySelectorAll('[data-lum-video] video')) {
      if (v !== keep && !v.paused) v.pause();
    }
  };

  class LumVideoRail extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('.lum-hrail__list');
      for (const card of this.querySelectorAll('[data-lum-video]')) this.#wireVideo(card);

      const prev = this.querySelector('[data-lum-prev]');
      const next = this.querySelector('[data-lum-next]');
      if (prev && next && this.list) {
        const step = (dir) => {
          const cell = this.list.querySelector('.lum-hrail__cell');
          const by = cell ? cell.getBoundingClientRect().width + 16 : this.list.clientWidth * 0.8;
          this.list.scrollBy({ left: dir * by, behavior: reduceMotion ? 'auto' : 'smooth' });
        };
        prev.addEventListener('click', () => step(-1));
        next.addEventListener('click', () => step(1));
        const update = () => {
          const max = this.list.scrollWidth - this.list.clientWidth - 2;
          prev.disabled = this.list.scrollLeft <= 2;
          next.disabled = this.list.scrollLeft >= max;
          this.classList.toggle('is-static', max <= 0);
        };
        this.list.addEventListener('scroll', update, { passive: true });
        window.addEventListener('resize', update, { passive: true });
        update();
      }

      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(([entry]) => {
          if (entry.isIntersecting) return;
          for (const v of this.querySelectorAll('video')) if (!v.paused) v.pause();
        });
        this.io.observe(this);
      }
    }

    disconnectedCallback() {
      this.io?.disconnect();
    }

    #wireVideo(card) {
      const video = card.querySelector('video');
      const play = card.querySelector('[data-lum-video-play]');
      const mute = card.querySelector('[data-lum-video-mute]');
      if (!video || !play) return;
      video.removeAttribute('controls');
      video.muted = true;
      // Sessiz kart (atölye videoları): ses hiçbir yoldan açılamaz (tarayıcı menüsü, kısayol vb.)
      if (card.hasAttribute('data-lum-video-silent')) {
        video.addEventListener('volumechange', () => {
          if (!video.muted) video.muted = true;
        });
      }

      const toggle = () => {
        if (video.paused) {
          pauseAllExcept(video);
          video.preload = 'auto';
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      };
      play.addEventListener('click', toggle);
      video.addEventListener('click', toggle);
      video.addEventListener('play', () => {
        card.classList.add('is-playing');
        play.setAttribute('aria-label', 'Videoyu durdur');
      });
      video.addEventListener('pause', () => {
        card.classList.remove('is-playing');
        play.setAttribute('aria-label', 'Videoyu oynat');
      });
      mute?.addEventListener('click', () => {
        video.muted = !video.muted;
        mute.setAttribute('aria-pressed', String(video.muted));
        mute.setAttribute('aria-label', video.muted ? 'Sesi aç' : 'Sesi kapat');
      });
    }
  }

  /* ---------------- Yorum verisi (Storefront API) ---------------- */
  // Tek kaynak custom.lum_yorumlar. Liquid bir listede yalnız ilk 50 kaydı verebildiği için listenin tamamı
  // burada okunur; puan özeti her sayfa açılışında bundan hesaplanır, böylece panelde yorum eklemek,
  // çıkarmak ya da puanını değiştirmek başka bir alanı güncellemeden yansır. Anahtar tema ayarından gelir
  // (Lumera ürün sayfası → Storefront anahtarı); yoksa sayfa Liquid çıktısıyla kalır.
  const REVIEW_QUERY = `query LumReviews($handle: String!, $after: String) {
    product(handle: $handle) {
      metafield(namespace: "custom", key: "lum_yorumlar") {
        references(first: 250, after: $after) {
          nodes { ... on Metaobject {
            yazar: field(key: "yazar") { value }
            sehir: field(key: "sehir") { value }
            puan: field(key: "puan") { value }
            metin: field(key: "metin") { value }
            dogrulanmis: field(key: "dogrulanmis") { value }
            gorsel: field(key: "gorsel") { reference { ... on MediaImage { image { url } } } }
          } }
          pageInfo { hasNextPage endCursor }
        }
      }
    }
  }`;

  let reviewsPromise;
  const loadReviews = () => (reviewsPromise ??= fetchReviews().catch(() => null));

  async function fetchReviews() {
    const el = document.getElementById('lum-review-config');
    const cfg = el ? JSON.parse(el.textContent) : null;
    if (!cfg?.token || !cfg.handle) return null;
    const list = [];
    let after = null;
    do {
      const res = await fetch(`/api/${cfg.version}/graphql.json`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': cfg.token },
        body: JSON.stringify({ query: REVIEW_QUERY, variables: { handle: cfg.handle, after } }),
      });
      if (!res.ok) throw new Error(`Storefront ${res.status}`);
      const json = await res.json();
      if (json.errors?.length) throw new Error(json.errors[0].message);
      const refs = json.data?.product?.metafield?.references;
      if (!refs) break;
      for (const n of refs.nodes) {
        if (!n || !('puan' in n)) continue;
        list.push({
          yazar: (n.yazar?.value || '').trim(),
          sehir: n.sehir?.value || '',
          puan: Number(n.puan?.value) || 0,
          metin: n.metin?.value || '',
          dogrulanmis: n.dogrulanmis?.value === 'true',
          gorsel: n.gorsel?.reference?.image?.url || '',
        });
      }
      after = refs.pageInfo.hasNextPage ? refs.pageInfo.endCursor : null;
    } while (after);
    return list;
  }

  /** Puan özetini ([data-lum-stats] kökleri) listenin tamamından yeniden yazar */
  function applyReviewStats(list) {
    const c = [0, 0, 0, 0, 0, 0];
    for (const r of list) if (r.puan >= 1 && r.puan <= 5) c[r.puan] += 1;
    const rated = c[1] + c[2] + c[3] + c[4] + c[5];
    const sum = c[1] + 2 * c[2] + 3 * c[3] + 4 * c[4] + 5 * c[5];
    const avg = rated ? Math.round((sum / rated) * 10) / 10 : 0;
    const avgText = avg.toLocaleString('tr-TR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    for (const root of document.querySelectorAll('[data-lum-stats]')) {
      root.querySelectorAll('[data-lum-stat-avg-text]').forEach((e) => (e.textContent = avgText));
      root.querySelectorAll('[data-lum-stat-count]').forEach((e) => (e.textContent = String(list.length)));
      root.querySelectorAll('.lum-stars__fill').forEach((e) => e.style.setProperty('--lum-stars-fill', `${((avg / 5) * 100).toFixed(1)}%`));
      root.querySelectorAll('[data-lum-stat-avg]').forEach((e) => (e.hidden = rated === 0));
      root.querySelectorAll('[data-lum-stat-bar]').forEach((li) => {
        const n = c[Number(li.dataset.lumStatBar)] || 0;
        const out = li.querySelector('[data-lum-stat-n]');
        if (out) out.textContent = String(n);
        li.querySelector('.lum-score__fill')?.style.setProperty('--lum-bar', rated ? (n / rated).toFixed(3) : '0');
      });
    }
  }

  const escapeHtml = (v) =>
    String(v).replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[ch]);

  const sizedImage = (url, width) => {
    try {
      const u = new URL(url, location.origin);
      u.searchParams.set('width', String(width));
      return u.toString();
    } catch {
      return url;
    }
  };

  const VERIFIED_HTML =
    '<span class="lum-review__verified"><svg viewBox="0 0 20 20" focusable="false" aria-hidden="true"><path d="M8.2 13.6 4.6 10l-1.1 1.1 4.7 4.7 9-9-1.1-1.1z"/></svg>Doğrulanmış alıcı</span>';

  /** snippets/lum-review-card.liquid (variant: grid) ile aynı işaretleme */
  function buildReviewCard(r, starTemplate) {
    const p = r.puan || 5;
    const name = r.yazar;
    const initial = name.replace(/^TEST · /, '').trim().slice(0, 1).toLocaleUpperCase('tr-TR');
    const card = document.createElement('article');
    card.className = 'lum-review lum-review--grid';
    card.dataset.rating = String(p);
    const media = r.gorsel
      ? `<button type="button" class="lum-review__media" data-lum-lightbox="${escapeHtml(sizedImage(r.gorsel, 1600))}" data-lum-lightbox-alt="${escapeHtml(name)} yorum görseli" aria-label="${escapeHtml(name)} kullanıcısının yorum görselini büyüt"><img src="${escapeHtml(sizedImage(r.gorsel, 720))}" srcset="${[360, 540, 720].map((w) => `${escapeHtml(sizedImage(r.gorsel, w))} ${w}w`).join(', ')}" sizes="(min-width: 990px) 360px, 80vw" loading="lazy" alt=""></button>`
      : '';
    card.innerHTML = `${media}<div class="lum-review__head"><span class="visually-hidden">5 üzerinden ${p} yıldız</span>${r.dogrulanmis ? VERIFIED_HTML : ''}</div><p class="lum-review__text">${escapeHtml(r.metin).replace(/\r?\n/g, '<br>')}</p><footer class="lum-review__foot"><span class="lum-review__avatar" aria-hidden="true">${escapeHtml(initial)}</span><span class="lum-review__who"><span class="lum-review__name">${escapeHtml(name)}</span>${r.sehir ? `<span class="lum-review__city">${escapeHtml(r.sehir)}</span>` : ''}</span></footer>`;
    if (starTemplate) {
      const stars = starTemplate.cloneNode(true);
      stars.querySelector('.lum-stars__fill')?.style.setProperty('--lum-stars-fill', `${((p / 5) * 100).toFixed(1)}%`);
      card.querySelector('.lum-review__head').prepend(stars);
    }
    return card;
  }

  if (document.getElementById('lum-review-config') && document.querySelector('[data-lum-stats]')) {
    loadReviews().then((list) => list && applyReviewStats(list));
  }

  /* ---------------- Yorum ızgarası ---------------- */
  // Masaüstünde ilk 25, mobilde ilk 6 yorum görünür; sonrakiler 25'erli (mobilde 10'arlı) gruplarla açılır.
  // Gizli kartlar da sütunlarda (sıra korunarak) tutulur; yeniden dizilimde DOM'dan düşmez.
  class LumReviewGrid extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-lum-grid]');
      this.cards = [...this.list.querySelectorAll('.lum-review')];
      this.mobile = this.list.clientWidth < 600;
      if (this.mobile) {
        this.cards.filter((c) => !c.hidden).slice(6).forEach((c) => {
          c.hidden = true;
          c.setAttribute('data-lum-review-extra', '');
        });
      }
      this.list.classList.add('is-masonry');
      this.cols = 0;
      this.layout();
      this.#status();
      this.ro = new ResizeObserver(() => this.layout());
      this.ro.observe(this);
      this.querySelector('[data-lum-more-btn]')?.addEventListener('click', (e) => this.#more(e.currentTarget));
      if (this.hasAttribute('data-lum-list')) this.#extend();
    }

    // Liste kaynağında sunucu ilk 50 kaydı verir; kalanlar aynı sırayla Storefront API'den eklenir.
    #extend() {
      return (this.extending ??= loadReviews().then((list) => {
        if (!list || !this.isConnected) return;
        const starTemplate = this.list.querySelector('.lum-review .lum-stars');
        const extra = list.slice(this.cards.length).map((r) => {
          const card = buildReviewCard(r, starTemplate);
          card.hidden = true;
          card.setAttribute('data-lum-review-extra', '');
          return card;
        });
        this.dataset.total = String(list.length);
        if (extra.length) {
          this.cards.push(...extra);
          this.layout(true);
        }
        this.#status();
      }));
    }

    disconnectedCallback() {
      this.ro?.disconnect();
      this.abort?.abort();
    }

    async #more(btn) {
      if (btn.disabled) return;
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
      const status = this.querySelector('[data-lum-more-status]');
      try {
        const step = this.mobile ? 10 : 25;
        if (this.hasAttribute('data-lum-list') && this.cards.filter((c) => c.hidden).length < step) await this.#extend();
        const fresh = this.cards.filter((c) => c.hidden).slice(0, step);
        if (!fresh.length) {
          this.querySelector('[data-lum-more]')?.remove();
          return;
        }
        for (const c of fresh) {
          c.hidden = false;
          c.classList.add('is-new');
        }
        const first = fresh[0];
        if (this.#status()) btn.disabled = false;
        const text = first?.querySelector('.lum-review__text');
        if (text) {
          text.setAttribute('tabindex', '-1');
          text.focus({ preventScroll: true });
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        btn.disabled = false;
        if (status) status.textContent = 'Yorumlar yüklenemedi. Tekrar deneyin.';
      } finally {
        btn.removeAttribute('aria-busy');
      }
    }

    // Durum metnini günceller; açılacak kart kalmadıysa düğmeyi kaldırır. Kalan kart varsa true döner.
    #status() {
      const shown = this.cards.filter((c) => !c.hidden).length;
      const remaining = this.cards.length - shown;
      const status = this.querySelector('[data-lum-more-status]');
      if (!remaining) {
        this.querySelector('[data-lum-more]')?.remove();
        return false;
      }
      if (status) status.textContent = `${shown} / ${this.dataset.total} yorum gösteriliyor`;
      return true;
    }

    layout(force = false) {
      const w = this.list.clientWidth;
      const cols = w >= 990 ? 3 : w >= 600 ? 2 : 1;
      if (cols === this.cols && !force) return;
      this.cols = cols;
      const columns = Array.from({ length: cols }, () => {
        const col = document.createElement('div');
        col.className = 'lum-rgrid__col';
        return col;
      });
      // Okuma sırası korunur: kartlar soldan sağa, satır satır dağıtılır
      this.cards.forEach((card, i) => columns[i % cols].append(card));
      this.list.replaceChildren(...columns);
    }
  }

  /* ---------------- Sayfa içi sepete ekle ---------------- */
  class LumCtaAtc extends HTMLElement {
    connectedCallback() {
      this.querySelector('[data-lum-atc]')?.addEventListener('click', () => {
        const main =
          document.querySelector('.product-information product-form-component button[name="add"]') ||
          document.querySelector('product-form-component button[name="add"]');
        if (main && !main.disabled) {
          main.click();
          return;
        }
        document.querySelector('.product-information')?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth' });
      });
    }
  }

  /* ---------------- Yorum görseli büyütme ---------------- */
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-lum-lightbox]');
    const dialog = document.querySelector('[data-lum-lightbox-dialog]');
    if (trigger && dialog) {
      const img = dialog.querySelector('[data-lum-lightbox-img]');
      img.src = trigger.dataset.lumLightbox;
      img.alt = trigger.dataset.lumLightboxAlt || '';
      dialog.showModal();
      return;
    }
    if (dialog?.open && (e.target === dialog || e.target.closest('[data-lum-lightbox-close]'))) dialog.close();
  });

  define('lum-price', LumPrice);
  define('lum-countdown', LumCountdown);
  define('lum-review-rail', LumReviewRail);
  define('lum-video-rail', LumVideoRail);
  define('lum-review-grid', LumReviewGrid);
  define('lum-cta-atc', LumCtaAtc);
})();
